<template>
  <div class="auth-form">
    <h2 class="auth-title">欢迎登录</h2>
    <p v-if="!loginEnabled" class="login-notice">系统维护中，暂时无法登录</p>
    <div class="input-group icon-input">
      <i class="fas fa-user"></i>
      <input type="text" placeholder="用户名" v-model="account" />
    </div>
    <div class="input-group icon-input">
      <i class="fas fa-lock"></i>
      <input type="password" placeholder="密码" v-model="password" @keyup.enter="login" />
    </div>
    <p v-if="loginError" class="login-error">{{ loginError }}</p>
    <!-- 空锚点：必须 prevent，否则 history 模式下点击会把 # 追加进 URL -->
    <a class="forgot-link" href="#" @click.prevent>忘记密码？</a>
    <button class="auth-submit" @click.prevent="login" :disabled="loginLoading || !loginEnabled">
      {{ !loginEnabled ? '系统维护中' : (loginLoading ? '登录中...' : '登录') }}
    </button>
    <p class="social-text">或使用社交账号登录</p>
    <div class="social-icons">
      <span class="social-icon">G</span>
      <span class="social-icon">f</span>
      <span class="social-icon">Git</span>
      <span class="social-icon">in</span>
    </div>
    <p v-if="registerEnabled" class="switch-text">
      还没有账号？
      <a href="#" @click.prevent="$emit('show-register')">立即注册</a>
    </p>
    <!-- 登录页也要能到达两份文档：注册时勾选的同意，事后必须随时可查阅 -->
    <p class="legal-links">
      <a href="/terms" target="_blank" rel="noopener">用户协议</a>
      <em>·</em>
      <a href="/privacy" target="_blank" rel="noopener">隐私政策</a>
    </p>
  </div>
</template>

<script>
import { login as loginApi } from '@/api/user.js'

export default {
  name: 'LoginForm',
  props: {
    prefillAccount: {
      type: String,
      default: ''
    },
    // 登录开关（来自后端 system_settings）：关闭时禁用提交并提示维护中
    loginEnabled: {
      type: Boolean,
      default: true
    },
    // 注册开关：关闭时隐藏「立即注册」入口
    registerEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      account: this.prefillAccount || '',
      password: '',
      loginError: '',
      loginLoading: false
    }
  },
  watch: {
    prefillAccount(val) {
      this.account = val || ''
    }
  },
  methods: {
    async login() {
      this.loginError = ''
      // 前端拦截只是体验优化，真正的裁决在后端 login() 入口（防止有人直接打接口）
      if (!this.loginEnabled) {
        this.loginError = '系统维护中，暂时无法登录'
        return
      }
      if (!this.account || !this.password) {
        this.loginError = '请输入账号和密码'
        return
      }
      this.loginLoading = true
      try {
        const data = await loginApi(this.account, this.password)
        if (!data.success || data.code !== 200) {
          this.loginError = data.message || '登录失败'
          return
        }
        this.$emit('login-success', data)
      } catch (e) {
        this.loginError = '网络异常，请稍后重试'
      } finally {
        this.loginLoading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-form{
  position:relative;
  height:100%;
  margin:0 auto;
  width:100%;
  max-width:384px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
}

.auth-title{
  font-size:2rem;
  font-weight:800;
  color:#333;
  margin-bottom:1.75rem;
}

.input-group{
  width:100%;
  margin-bottom:0.875rem;
}

.input-group input{
  width:100%;
  padding:0.875rem 1rem;
  border:none;
  border-radius:0.625rem;
  background:#f0f0f0;
  font-size:0.875rem;
  color:#333;
  font-family:inherit;
  outline:none;
}

.input-group input::placeholder{
  color:#999;
}

.login-error{
  width:100%;
  margin-bottom:0.75rem;
  font-size:0.75rem;
  color:#e74c3c;
  text-align:left;
}

.login-notice{
  width:100%;
  margin-bottom:1rem;
  padding:0.625rem 0.75rem;
  border-radius:0.5rem;
  background:#fff6e5;
  border:1px solid #f0d9a8;
  font-size:0.75rem;
  color:#8a6d1f;
  text-align:left;
}

.icon-input{
  position:relative;
}

.icon-input i{
  position:absolute;
  left:1rem;
  top:50%;
  transform:translateY(-50%);
  color:#999;
  font-size:0.875rem;
  z-index:1;
}

.icon-input input{
  padding-left:2.5rem;
}

.forgot-link{
  align-self:flex-end;
  font-size:0.75rem;
  color:#666;
  text-decoration:none;
  margin-bottom:1.125rem;
}

.forgot-link:hover{
  color:#558b2f;
}

.auth-submit{
  width:100%;
  padding:0.875rem;
  border:none;
  border-radius:0.625rem;
  background:#558b2f;
  color:#fff;
  font-size:0.9375rem;
  font-weight:700;
  cursor:pointer;
  font-family:inherit;
  margin-bottom:1.125rem;
  transition:background 0.2s ease;
}

.auth-submit:hover{
  background:#4a7c28;
}

.auth-submit:disabled{
  background:#9abf7a;
  cursor:not-allowed;
}

.social-text{
  font-size:0.75rem;
  color:#888;
  margin-bottom:0.875rem;
}

.social-icons{
  display:flex;
  gap:0.75rem;
  margin-bottom:1.125rem;
}

.social-icon{
  width:40px;
  height:40px;
  border:1px solid #ddd;
  border-radius:0.5rem;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:0.8125rem;
  font-weight:700;
  color:#555;
  cursor:pointer;
  transition:all 0.2s ease;
}

.social-icon:hover{
  border-color:#558b2f;
  color:#558b2f;
}

.switch-text{
  font-size:0.75rem;
  color:#888;
}

.switch-text a{
  color:#558b2f;
  font-weight:700;
  text-decoration:none;
}

/* 协议链接：比 switch-text 更淡，只作为可查阅入口，不抢主操作视觉 */
.legal-links{
  margin-top:0.75rem;
  font-size:0.7rem;
  color:#b0b0b0;
}

.legal-links a{
  color:#999;
  text-decoration:none;
}

.legal-links a:hover{
  color:#558b2f;
}

.legal-links em{
  font-style:normal;
  margin:0 0.4rem;
  color:#d0d0d0;
}
</style>
